import {Text} from '@react-three/drei';
import {Interactive} from '@react-three/xr';
import {Layer} from '../../types/layers';

interface VRSettingsMenuProps {
  visible: boolean;
  mode: 'position' | 'settings';
  onModeChange: (mode: 'position' | 'settings') => void;
  onClose: () => void;
  onEndExercise?: () => void;
  onPositionMove: (direction: 'up' | 'down' | 'left' | 'right' | 'closer' | 'farther') => void;
  onLeftEyeColorChange: (color: string) => void;
  onRightEyeColorChange: (color: string) => void;
  onLeftEyeOverlayToggle: () => void;
  onRightEyeOverlayToggle: () => void;
  onSpeedChange: (speed: number) => void;
  onRangeChange: (range: 'narrow' | 'medium' | 'wide') => void;
  leftEyeColor: string;
  rightEyeColor: string;
  leftEyeOverlay: boolean;
  rightEyeOverlay: boolean;
  currentSpeed: number;
  currentRange: 'narrow' | 'medium' | 'wide';
  timeRemaining: number;
}

export const VRSettingsMenu: React.FC<VRSettingsMenuProps> = ({
  visible,
  mode,
  onModeChange,
  onClose,
  onEndExercise,
  onPositionMove,
  onLeftEyeColorChange,
  onRightEyeColorChange,
  onLeftEyeOverlayToggle,
  onRightEyeOverlayToggle,
  onSpeedChange,
  onRangeChange,
  leftEyeColor,
  rightEyeColor,
  leftEyeOverlay,
  rightEyeOverlay,
  currentSpeed,
  currentRange,
}) => {
  if (!visible) {
    return null;
  }

  const handleMove = (direction: 'up' | 'down' | 'left' | 'right' | 'closer' | 'farther') => {
    onPositionMove(direction);
  };

  const handleLeftEyeColorCycle = () => {
    const colors = ['red', 'lime', 'blue', 'yellow', 'white'];
    const currentIndex = colors.indexOf(leftEyeColor);
    const newColor = colors[(currentIndex + 1) % colors.length];
    onLeftEyeColorChange(newColor);
  };

  const handleRightEyeColorCycle = () => {
    const colors = ['red', 'lime', 'blue', 'yellow', 'white'];
    const currentIndex = colors.indexOf(rightEyeColor);
    const newColor = colors[(currentIndex + 1) % colors.length];
    onRightEyeColorChange(newColor);
  };

  const handleSpeedDecrease = () => {
    const newSpeed = Math.max(0, currentSpeed - 1.5);
    onSpeedChange(newSpeed);
  };

  const handleSpeedIncrease = () => {
    const newSpeed = Math.min(15, currentSpeed + 1.5);
    onSpeedChange(newSpeed);
  };

  const handleRangeCycle = () => {
    const ranges: ('narrow' | 'medium' | 'wide')[] = ['narrow', 'medium', 'wide'];
    const currentIndex = ranges.indexOf(currentRange);
    const newRange = ranges[(currentIndex + 1) % 3];
    onRangeChange(newRange);
  };

  const handleModeSwitch = () => {
    const newMode = mode === 'position' ? 'settings' : 'position';
    onModeChange(newMode);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <group position={[0, 0, 0]}>
      {/* Position Mode - Buttons around sphere */}
      {mode === 'position' && (
        <>
          {/* Up Button */}
          <Interactive onSelect={() => handleMove('up')}>
            <mesh position={[0, 0.15, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.1, 0.1]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0, 0.15, 0.01]} scale={0.05} color="white" anchorX="center" anchorY="middle">
            ▲
          </Text>

          {/* Down Button */}
          <Interactive onSelect={() => handleMove('down')}>
            <mesh position={[0, -0.15, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.1, 0.1]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0, -0.15, 0.01]} scale={0.05} color="white" anchorX="center" anchorY="middle">
            ▼
          </Text>

          {/* Left Button */}
          <Interactive onSelect={() => handleMove('left')}>
            <mesh position={[-0.15, 0, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.1, 0.1]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[-0.15, 0, 0.01]} scale={0.05} color="white" anchorX="center" anchorY="middle">
            ◄
          </Text>

          {/* Right Button */}
          <Interactive onSelect={() => handleMove('right')}>
            <mesh position={[0.15, 0, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.1, 0.1]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0.15, 0, 0.01]} scale={0.05} color="white" anchorX="center" anchorY="middle">
            ►
          </Text>

          {/* Farther Button - Bottom Left */}
          <Interactive onSelect={() => handleMove('farther')}>
            <mesh position={[-0.18, -0.25, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.14, 0.08]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[-0.18, -0.25, 0.01]} scale={0.028} color="white" anchorX="center" anchorY="middle">
            FARTHER
          </Text>

          {/* Closer Button - Bottom Right */}
          <Interactive onSelect={() => handleMove('closer')}>
            <mesh position={[0.18, -0.25, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.14, 0.08]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0.18, -0.25, 0.01]} scale={0.028} color="white" anchorX="center" anchorY="middle">
            CLOSER
          </Text>

          {/* Settings Button - Top Right */}
          <Interactive onSelect={handleModeSwitch}>
            <mesh position={[0.18, 0.25, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.14, 0.08]} />
              <meshBasicMaterial color="#8855ff" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0.18, 0.25, 0.01]} scale={0.028} color="white" anchorX="center" anchorY="middle">
            SETTINGS
          </Text>

          {/* Close Button - Top Left */}
          <Interactive onSelect={handleClose}>
            <mesh position={[-0.18, 0.25, 0]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.14, 0.08]} />
              <meshBasicMaterial color="#cc3333" side={2} />
            </mesh>
          </Interactive>
          <Text position={[-0.18, 0.25, 0.01]} scale={0.028} color="white" anchorX="center" anchorY="middle">
            CLOSE
          </Text>
        </>
      )}

      {/* Settings Mode UI - Popup Panel */}
      {mode === 'settings' && (
        <group position={[0, 0, 0.3]}>
          {/* Background panel */}
          <mesh position={[0, 0, 0]} layers={Layer.DEFAULT}>
            <planeGeometry args={[0.6, 0.65]} />
            <meshBasicMaterial color="#1a1a1a" side={2} />
          </mesh>

          {/* Header */}
          <mesh position={[0, 0.26, 0.01]} layers={Layer.DEFAULT}>
            <planeGeometry args={[0.55, 0.06]} />
            <meshBasicMaterial color="#ff8800" side={2} />
          </mesh>
          <Text position={[0, 0.26, 0.02]} scale={0.03} color="white" anchorX="center" anchorY="middle">
            SETTINGS
          </Text>

          {/* Left Eye Section */}
          <Text position={[-0.15, 0.18, 0.01]} scale={0.025} color="#aaaaaa" anchorX="center" anchorY="middle">
            LEFT EYE
          </Text>

          {/* Left Eye Color */}
          <Interactive onSelect={handleLeftEyeColorCycle}>
            <mesh position={[-0.15, 0.11, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.22, 0.06]} />
              <meshBasicMaterial color={leftEyeColor} side={2} />
            </mesh>
          </Interactive>
          <Text position={[-0.15, 0.11, 0.02]} scale={0.022} color="black" anchorX="center" anchorY="middle">
            COLOR
          </Text>

          {/* Left Eye Overlay Toggle */}
          <Interactive onSelect={onLeftEyeOverlayToggle}>
            <mesh position={[-0.15, 0.04, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.22, 0.06]} />
              <meshBasicMaterial color={leftEyeOverlay ? '#4488ff' : '#333333'} side={2} />
            </mesh>
          </Interactive>
          <Text position={[-0.15, 0.04, 0.02]} scale={0.022} color="white" anchorX="center" anchorY="middle">
            X: {leftEyeOverlay ? 'ON' : 'OFF'}
          </Text>

          {/* Right Eye Section */}
          <Text position={[0.15, 0.18, 0.01]} scale={0.025} color="#aaaaaa" anchorX="center" anchorY="middle">
            RIGHT EYE
          </Text>

          {/* Right Eye Color */}
          <Interactive onSelect={handleRightEyeColorCycle}>
            <mesh position={[0.15, 0.11, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.22, 0.06]} />
              <meshBasicMaterial color={rightEyeColor} side={2} />
            </mesh>
          </Interactive>
          <Text position={[0.15, 0.11, 0.02]} scale={0.022} color="black" anchorX="center" anchorY="middle">
            COLOR
          </Text>

          {/* Right Eye Overlay Toggle */}
          <Interactive onSelect={onRightEyeOverlayToggle}>
            <mesh position={[0.15, 0.04, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.22, 0.06]} />
              <meshBasicMaterial color={rightEyeOverlay ? '#4488ff' : '#333333'} side={2} />
            </mesh>
          </Interactive>
          <Text position={[0.15, 0.04, 0.02]} scale={0.022} color="white" anchorX="center" anchorY="middle">
            X: {rightEyeOverlay ? 'ON' : 'OFF'}
          </Text>

          {/* Speed Label */}
          <Text position={[0, -0.04, 0.01]} scale={0.025} color="#aaaaaa" anchorX="center" anchorY="middle">
            SPEED
          </Text>

          {/* Speed Decrease */}
          <Interactive onSelect={handleSpeedDecrease}>
            <mesh position={[-0.12, -0.1, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.08, 0.07]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[-0.12, -0.1, 0.02]} scale={0.04} color="white" anchorX="center" anchorY="middle">
            -
          </Text>

          {/* Speed Display */}
          <Text position={[0, -0.1, 0.01]} scale={0.028} color="white" anchorX="center" anchorY="middle">
            {currentSpeed.toFixed(1)}
          </Text>

          {/* Speed Increase */}
          <Interactive onSelect={handleSpeedIncrease}>
            <mesh position={[0.12, -0.1, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.08, 0.07]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0.12, -0.1, 0.02]} scale={0.04} color="white" anchorX="center" anchorY="middle">
            +
          </Text>

          {/* Range Button */}
          <Interactive onSelect={handleRangeCycle}>
            <mesh position={[0, -0.18, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.35, 0.07]} />
              <meshBasicMaterial color="#3366cc" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0, -0.18, 0.02]} scale={0.028} color="white" anchorX="center" anchorY="middle">
            RANGE: {currentRange.toUpperCase()}
          </Text>

          {/* End Exercise Button - Red */}
          {onEndExercise && (
            <>
              <Interactive onSelect={onEndExercise}>
                <mesh position={[0, -0.245, 0.01]} layers={Layer.DEFAULT}>
                  <planeGeometry args={[0.35, 0.06]} />
                  <meshBasicMaterial color="#cc3333" side={2} />
                </mesh>
              </Interactive>
              <Text position={[0, -0.245, 0.02]} scale={0.025} color="white" anchorX="center" anchorY="middle">
                END EXERCISE
              </Text>
            </>
          )}

          {/* Back Button */}
          <Interactive onSelect={handleModeSwitch}>
            <mesh position={[0, -0.31, 0.01]} layers={Layer.DEFAULT}>
              <planeGeometry args={[0.35, 0.06]} />
              <meshBasicMaterial color="#8855ff" side={2} />
            </mesh>
          </Interactive>
          <Text position={[0, -0.31, 0.02]} scale={0.025} color="white" anchorX="center" anchorY="middle">
            BACK
          </Text>
        </group>
      )}
    </group>
  );
};
